import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Navbar from '../../components/layout/Navbar';
import DataTable from '../../components/common/DataTable';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { getOwners, toggleOwner, deleteOwner, getGroups, createOwner } from '../../api/superAdminApi';
import { UserCheck, Eye, Power, Trash2, Phone, Mail, FolderOpen, Plus, Lock } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const ManageOwners = () => {
  const [owners, setOwners]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modalType, setModalType] = useState(null); // 'view' | 'delete' | 'add'
  const [ownerGroups, setOwnerGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });

  const fetchOwners = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getOwners();
      setOwners(res.data?.data?.owners || []);
    } catch {
      toast.error('Failed to load owners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOwners(); }, [fetchOwners]);

  const handleToggle = async (owner) => {
    try {
      await toggleOwner(owner.user_id, { is_active: !owner.is_active });
      toast.success(`${owner.name} ${owner.is_active ? 'deactivated' : 'activated'}`);
      fetchOwners();
    } catch {
      toast.error('Failed to toggle owner status');
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await deleteOwner(selected.user_id);
      toast.success(`${selected.name} deleted`);
      setModalType(null); setSelected(null);
      fetchOwners();
    } catch {
      toast.error('Failed to delete owner');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddOwner = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.password) {
      return toast.error('Name, phone, and password are required');
    }
    try {
      setActionLoading(true);
      await createOwner(formData);
      toast.success('Owner created successfully');
      setModalType(null);
      setFormData({ name: '', email: '', phone: '', password: '' });
      fetchOwners();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create owner');
    } finally {
      setActionLoading(false);
    }
  };

  const handleView = async (owner) => {
    setSelected(owner);
    setModalType('view');
    setLoadingGroups(true);
    try {
      const res = await getGroups();
      const allGroups = res.data?.data?.groups || [];
      setOwnerGroups(allGroups.filter(g => g.created_by === owner.user_id || g.creator?.user_id === owner.user_id));
    } catch {
      toast.error('Failed to load owner groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const columns = [
    { header: '#',       id: 'no',      cell: ({ row: { original: o } }) => (
      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
        <span className="text-blue-700 text-xs font-bold">{o.name?.charAt(0).toUpperCase()}</span>
      </div>
    )},
    { header: 'Name',    accessorKey: 'name',  cell: ({ row: { original: o } }) => (
      <div><p className="font-semibold text-gray-900">{o.name}</p><p className="text-xs text-gray-400">{o.email}</p></div>
    )},
    { header: 'Phone',   accessorKey: 'phone' },
    { header: 'Groups Created',  accessorKey: 'groups_count', cell: ({ row: { original: o } }) => (
      <span className="font-bold text-blue-700">{o.groups_count || 0}</span>
    )},
    { header: 'Active Groups',  accessorKey: 'active_groups', cell: ({ row: { original: o } }) => (
      <span className="font-bold text-teal-600">{o.active_groups || 0}</span>
    )},
    { header: 'Joined Date',  accessorKey: 'createdAt', cell: ({ row: { original: o } }) => (
      <span className="text-xs text-gray-500">{o.createdAt ? format(new Date(o.createdAt), 'dd MMM yyyy') : '—'}</span>
    )},
    { header: 'Actions', id: 'actions', cell: ({ row: { original: o } }) => (
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleView(o)} />
        <Button size="sm" variant={o.is_active ? 'danger' : 'success'} icon={Power}
          onClick={() => handleToggle(o)}>
          {o.is_active ? 'Suspend' : 'Activate'}
        </Button>
      </div>
    )},
  ];

  return (
    <Layout>
      <Navbar title="Manage Owners" subtitle={`${owners.length} registered owners`} onRefresh={fetchOwners} loading={loading} />
      <div className="p-6">
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-xl"><UserCheck size={20} className="text-blue-600" /></div>
              <div>
                <h2 className="font-bold text-gray-900">Owner Registry</h2>
                <p className="text-xs text-gray-500">Manage all bhishi group owners</p>
              </div>
            </div>
            <Button icon={Plus} onClick={() => setModalType('add')}>Add Owner</Button>
          </div>
          <DataTable columns={columns} data={owners} loading={loading} emptyMessage="No owners found" />
        </div>
      </div>

      {/* View Modal */}
      <Modal open={modalType === 'view'} onClose={() => setModalType(null)} title="Owner Details & Groups">
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-700">{selected.name?.charAt(0)}</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selected.name}</h3>
                <Badge status={selected.is_active ? 'active' : 'inactive'} />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Email', value: selected.email, icon: Mail },
                { label: 'Phone', value: selected.phone, icon: Phone },
                { label: 'Joined', value: selected.createdAt ? format(new Date(selected.createdAt), 'dd MMM yyyy') : '—', icon: null },
              ].map(item => (
                <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-800">{item.value}</p>
                </div>
              ))}
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FolderOpen size={16} className="text-purple-600" />
                Owner's Groups
              </h4>
              {loadingGroups ? (
                <p className="text-sm text-gray-500">Loading groups...</p>
              ) : ownerGroups.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-50 p-4 rounded-xl text-center border border-dashed border-gray-200">No groups created yet.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {ownerGroups.map(g => (
                    <div key={g.group_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <div>
                        <p className="text-sm font-bold text-gray-900">{g.group_name}</p>
                        <p className="text-xs text-gray-500">₹{g.amount} • {g.duration_months}mo</p>
                      </div>
                      <Badge status={g.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Modal */}
      <Modal open={modalType === 'delete'} onClose={() => setModalType(null)} title="Confirm Delete" size="sm">
        {selected && (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <Trash2 size={24} className="text-red-600" />
            </div>
            <p className="text-gray-700">Are you sure you want to delete <strong>{selected.name}</strong>? This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setModalType(null)} className="flex-1">Cancel</Button>
              <Button variant="danger" onClick={handleDelete} loading={actionLoading} className="flex-1" icon={Trash2}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Owner Modal */}
      <Modal open={modalType === 'add'} onClose={() => setModalType(null)} title="Add New Owner" size="md">
        <form onSubmit={handleAddOwner} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <div className="relative">
              <UserCheck size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                required
                className="input pl-10"
                placeholder="John Doe"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                className="input pl-10"
                placeholder="john@example.com"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
            <div className="relative">
              <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                required
                className="input pl-10"
                placeholder="+91 9876543210"
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                required
                className="input pl-10"
                placeholder="Secure password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <Button type="button" variant="secondary" onClick={() => setModalType(null)} className="flex-1">Cancel</Button>
            <Button type="submit" loading={actionLoading} className="flex-1">Create Owner</Button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
};

export default ManageOwners;
